import type { RngState } from './rng';
import type { StartSlot } from '../content/maps';
import type { UnitTypeId } from '../content/units';

/*
 * מצב המשחק.
 *
 * שלוש תכונות שאינן סגנון אלא תנאי קיום:
 *   1. **הכול מספרים שלמים.** אין float, אין Date, אין מצביעים לאובייקטים חיצוניים.
 *      זה מה שמאפשר לסריאליזציה להיות מדויקת ול-hash להיות משמעותי.
 *   2. **מזהים עולים ולא אינדקסים.** יחידות נוצרות ונהרסות כל הזמן; אינדקס במערך
 *      היה משתנה תחת הרגליים של כל פקודה ששמורה לטיק עתידי.
 *   3. **readonly לאורך כל הדרך.** `step` בונה טיוטות פנימיות ומחזיר מצב חדש —
 *      אף אחד לא כותב לתוך מצב קיים, אחרת האינטרפולציה בין prev ל-cur נשברת.
 */

export type UnitId = number;

/*
 * מצב היחידה.
 *
 * החליף בורר "עמדות" בן שלושה מצבים שהיה בממשק. **ב-RA2 אין בורר כזה** — יחידה
 * שם יורה במה שנכנס לטווח ורודפת מרחק קצר, וזהו. הבורר הגיע מ-Generals ומ-OpenRA,
 * והוא היה יותר מכניקה ממה שהמשחק צריך: הבעיה האמיתית שהוא פתר היא אחת ומוגדרת —
 * שיחידה לא תיגרר אחרי צופה בודד לתוך מלכודת — ולזה יש פתרון אלגנטי יותר, רצועה.
 *
 * המצב נקבע מפקודה ולא ממתג שצריך לזכור באיזה מצב הוא.
 */
export const UnitMode = {
    /** עומד. יורה במה שבטווח ורודף עד גבול הרצועה מהעוגן. */
    Idle: 0,
    /** בדרך ליעד. יורה תוך כדי אבל לא סוטה מהמסלול. */
    Moving: 1,
    /** מתקדם ליעד ומחסל כל מה שבדרך. */
    AttackMove: 2,
    /** פקודת תקיפה מפורשת — רודף בלי רצועה, כי השחקן ביקש. */
    Attacking: 3,
    /** עצור: יורה רק במה שנכנס לטווח, לעולם לא זז מיוזמתו. */
    Holding: 4,
    /** קוצר בדרך לשדה או כורה בו. */
    Harvesting: 5,
    /** קוצר עמוס בדרך למפעל העיבוד. */
    Returning: 6,
} as const;
export type UnitMode = (typeof UnitMode)[keyof typeof UnitMode];

/**
 * עד כמה יחידה מוכנה להתרחק מהעוגן שלה כדי לרדוף אחרי מטרה שרכשה בעצמה.
 * זה מה שמונע ממשמר להיגרר אחרי צופה בודד ולנטוש את מה שהוא שומר עליו.
 */
export const LEASH_DISTANCE = 4 * 256;

export interface Unit {
    readonly id: UnitId;
    readonly owner: number;
    readonly type: UnitTypeId;
    /** מיקום ביחידות עולם (TILE ליחידת אריח). */
    readonly x: number;
    readonly y: number;
    readonly hp: number;
    readonly mode: UnitMode;
    /** אריח היעד הסופי, או -1. */
    readonly goalTile: number;
    /** אריחי המסלול שנותרו, מהבא ועד היעד. ריק = אין מסלול. */
    readonly path: readonly number[];
    /** העוגן שממנו נמדדת הרצועה — המקום שבו היחידה "שייכת". */
    readonly homeX: number;
    readonly homeY: number;
    /** מטרה נוכחית, או -1. */
    readonly targetId: UnitId;
    /** טיקים עד הירייה הבאה. */
    readonly cooldown: number;
    /** יריות שנותרו במטח הנוכחי. */
    readonly burstLeft: number;
    /** עפרות שהיחידה נושאת. רלוונטי לקוצר בלבד. */
    readonly cargo: number;
}

export interface Building {
    readonly id: number;
    readonly owner: number;
    readonly type: number;
    /**
     * אריח נקודת ההתכנסות, או ‎-1.
     *
     * **לכל מבנה בנפרד ולא לשחקן.** מפעל רק"ם ששולח טנקים לחזית וקסרקטין ששולח
     * חי"ר לשמור על הבסיס הם שתי כוונות שונות, וב-RA2 הן אכן נפרדות.
     */
    readonly rallyTile: number;
    /**
     * המבנה מסומן לתיקון: אשראי נמשך לאט וה-hp עולה לאט.
     *
     * **בלי זה כל נזק למבנה קבוע לתמיד**, וכל קרב הוא שחיקה חד-כיוונית — אין
     * שום סיבה לסגת ולהתאושש, יש רק "לתקוף עד שאחד נופל".
     */
    readonly repairing: number;
    /** פינת המבנה — הוא תופס w×h אריחים ממנה ימינה ולמטה. */
    readonly tileX: number;
    readonly tileY: number;
    readonly hp: number;
    readonly targetId: UnitId;
    readonly cooldown: number;
    readonly burstLeft: number;
}

export interface Projectile {
    readonly id: number;
    readonly owner: number;
    readonly type: number;
    readonly x: number;
    readonly y: number;
    /** עוקב אחרי המטרה. אם היא מתה — הקליע ממשיך לנקודה האחרונה ומתפוגג. */
    readonly targetId: UnitId;
    readonly lastX: number;
    readonly lastY: number;
    readonly damage: number;
    readonly warhead: number;
}

/*
 * אירועי טיק — פלט בלבד לרנדרר (פיצוץ, ירייה, מוות).
 *
 * **חריג מכוון לכלל "כל שדה נכנס ל-hash ול-serialize".**
 * הם נגזרים מהחישוב של אותו טיק, אף פעם לא נקראים ע"י `step`, ולא משפיעים על אף
 * טיק עתידי. לכן הם לא נשמרים ולא מגובבים — משחק שנטען ימשיך בדיוק אותו דבר גם
 * בלי האירועים של הטיק האחרון. אם מישהו אי פעם **יקרא** מהם בסימולציה, הכלל חוזר
 * לתוקף והם חייבים להיכנס לשניהם.
 */
export const EventKind = {
    Shot: 0,
    Impact: 1,
    Death: 2,
    /** יחידה יצאה מהמפעל. פלט לרנדרר בלבד — צליל והודעה, לא לוגיקה. */
    Spawned: 3,
} as const;
export type EventKind = (typeof EventKind)[keyof typeof EventKind];

export interface SimEvent {
    readonly kind: EventKind;
    readonly x: number;
    readonly y: number;
    /** לירייה: כיוון הלוע. לפגיעה: סוג ה-warhead. למוות וללידה: סוג היחידה. */
    readonly a: number;
    readonly b: number;
}

export interface ProductionItem {
    readonly kind: number;
    readonly type: number;
    readonly paid: number;
    readonly ready: boolean;
}

export interface PlayerState {
    readonly index: number;
    readonly kind: 'human' | 'ai' | 'remote';
    readonly credits: number;
    readonly defeated: boolean;
    /**
     * רמת הקושי של היריב. אינדקס ב-content/difficulties, מתעלמים ממנו לשחקן אנושי.
     *
     * הוא יושב **בתוך המצב** ולא בקוד הממשק כדי ש"המשך משחק" יחזיר את היריב כפי
     * שהיה. ה-AI עצמו אינו מחזיק שום מצב — הוא נגזר מהמצב, ולכן זה כל מה שצריך.
     */
    readonly difficulty: number;
    /**
     * מה שהשחקן **כבר ראה** — בית לאריח, 1 = נחשף.
     *
     * זהו הזיכרון של ערפל הקרב, ולכן הוא במצב ולא בשכבת התצוגה: אי אפשר לגזור
     * אותו ממצב רגעי, והוא חייב לשרוד "המשך משחק". מה שנראה **עכשיו** דווקא
     * כן נגזר — ראו `sim/vision.ts`.
     */
    readonly explored: Uint8Array;
    /**
     * מונים לסיכום הקרב: כמה יחידות ייצרתי, וכמה מהן איבדתי.
     *
     * הם **במצב** ולא בשכבת התצוגה כי מסך סיום שמאבד את המספרים ב"המשך משחק"
     * גרוע ממסך סיום בלי מספרים — הוא משקר.
     */
    readonly unitsBuilt: number;
    readonly unitsLost: number;
    /**
     * **תור לכל מבנה ייצור**, לפי הסדר של `content/production.ts`.
     *
     * זו הצורה של RA2: הקסרקטין מאמן חי"ר בזמן שהמפעל בונה טנק, והמפקדה בונה
     * מבנים — שלושתם במקביל. קודם היו שני תורים בלבד, ולכן שישה טנקים בתור
     * חסמו הזמנת חייל, ו"לבנות קסרקטין שני" היה קישוט ולא החלטה כלכלית.
     *
     * האינדקס הוא מזהה מתמשך שנשמר גם בשמירה — ראו האזהרה ב-`production.ts`.
     */
    readonly queues: readonly (readonly ProductionItem[])[];
}

export const Phase = {
    Playing: 0,
    Ended: 1,
} as const;
export type Phase = (typeof Phase)[keyof typeof Phase];

export interface GameState {
    readonly tick: number;
    readonly rng: RngState;
    /**
     * ה-seed שממנו נבנה המשחק. נשמר במצב ולא רק בקונפיג, כי הוא נחוץ אחרי טעינה:
     * ה-AI מגריל מ-`deriveRng(seed, player, tick)` חסר-המצב, ובלי ה-seed הוא היה
     * ממשיך אחרי טעינה בזרם אחר. הוא גם מה שיאפשר replay מ-{seed, פקודות}.
     */
    readonly seed: number;
    readonly nextEntityId: number;
    readonly mapId: string;
    /**
     * נקודות הפתיחה של המשחק הזה — **מוגרלות, ולא מה שכתוב במפה.**
     *
     * הן במצב ולא נגזרות מחדש כי הן נדרשות אחרי טעינה בדיוק כמו לפניה: ה-AI
     * מדרג יעדים לפי נקודת הפתיחה של היריב, ופריסת הבסיס נגזרת מהסיבוב שלהן.
     * ראו `sim/starts.ts` — ההגרלה משמרת את סימטריית המפה, ולכן היא אינה נוגעת
     * בהוגנות.
     */
    readonly startSlots: readonly StartSlot[];
    /**
     * שכבת העפרות. משותפת בהפניה כל עוד איש לא כרה — העתקה של 9,216 ערכים בכל טיק
     * הייתה בזבוז מיותר. הרגע שמישהו כן כורה, המערך מועתק פעם אחת באותו טיק.
     */
    readonly ore: Uint16Array;
    readonly units: readonly Unit[];
    readonly buildings: readonly Building[];
    readonly projectiles: readonly Projectile[];
    readonly players: readonly PlayerState[];
    readonly phase: Phase;
    /** אינדקס המנצח, או -1. */
    readonly winner: number;
    /** בקשות מסלול שממתינות לתקציב. נשמר במצב כדי שגם הוא ישוחזר משמירה. */
    readonly pathQueue: readonly UnitId[];
    /** אירועי הטיק האחרון — פלט לרנדרר בלבד. ראו ההערה ליד SimEvent. */
    readonly events: readonly SimEvent[];
}

/** מה שצריך כדי לשחזר משחק מאפס: אותם ערכים תמיד נותנים אותו משחק. */
export interface MatchConfig {
    readonly seed: number;
    readonly mapId: string;
    readonly slots: readonly PlayerSlot[];
    /**
     * נקודות פתיחה מפורשות במקום ההגרלה.
     *
     * **קיים בשביל מדידה, לא בשביל משחק.** טסט שמודד התנהגות של ערפל או של
     * איזון רוצה פריסה **קבועה** — אחרת כל מספר שהוא מייצר הוא גם מדידה של
     * ההגרלה, והסף שנקבע לפיו נשבר בהגרלה הבאה. במשחק אמיתי השדה אינו נמסר
     * ונקודות הפתיחה מוגרלות (`sim/starts.ts`).
     */
    readonly startSlots?: readonly StartSlot[];
}

export interface PlayerSlot {
    readonly kind: 'human' | 'ai' | 'remote';
    /** רמת קושי ליריב מחשב. ראו content/difficulties. */
    readonly difficulty?: number;
}
